import moment from 'moment-timezone';
import { underscore } from '@ember/string';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util — pure helper functions consumed by the application
 *              controller (time formatting, relation grouping, lastSavedRecord
 *              bookkeeping, idle timeout, LiveChat "fresh properties")
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      moment-timezone, @ember/string (underscore)
 *   siblings:  -
 *   tests:     application-helpers-test.js (unit)
 *
 * Grab-bag of pure, framework-light functions factored out of
 * controllers/application.js so they can be unit-tested without a running
 * controller. Every export takes its inputs as plain arguments and returns a
 * new value — none of them read or write `this`.
 */

/**
 * Screensaver idle time. `option_field.screensaver.idle_time` is stored in
 * MINUTES: settings/gdpr edits it as minutes and calculateIdleTimeout converts
 * it to ms. DEFAULT_IDLE_TIMEOUT is the same default already in ms.
 */
const DEFAULT_IDLE_MINUTES = 30;
const DEFAULT_IDLE_TIMEOUT = DEFAULT_IDLE_MINUTES * 60000;

/**
 * Upper bound (one week) for a stored idle_time to be read as minutes.
 *
 * Root cause of values above it: settings/gdpr used to seed a missing
 * idle_time with 1800000 (the ms default) into a minutes field, so a tenant
 * that saved Settings > GDPR without touching the field persisted 1800000
 * MINUTES (~3.4 years) and the idle lock never fired. The seed is fixed
 * (default is now DEFAULT_IDLE_MINUTES), but those rows are already stored.
 * Such a value is never a real minutes choice (turning the lock off is the
 * `disabled` flag), so it falls back to the default instead of being
 * re-interpreted as ms: for the known polluted value (1800000) both readings
 * give 30 minutes, and for anything else a ms reading could produce a
 * seconds-long lock. A settings/gdpr save rewrites the healed value.
 */
const MAX_IDLE_MINUTES = 7 * 24 * 60;

/** Maximum number of saved records to track */
const LAST_SAVED_RECORD_MAX = 5;

/** Shortcut key -> route mapping */
const ROUTE_MAPPING = {
  0: 'index',
  1: 'contacts',
  2: 'products',
  3: 'projects',
  4: 'calendar',
  5: 'events',
  6: 'estimates',
  7: 'invoices',
  8: 'expenses',
  9: 'payments'
};

/** Quill Editor toolbar configuration */
const QUILL_TOOLBAR_CONFIG = [[{
  size: ['small', false, 'large', 'huge']
}], ['bold', 'italic', 'underline', 'strike'], [{
  color: []
}, {
  background: []
}], [{
  list: 'ordered'
}, {
  list: 'bullet'
}], [{
  align: []
}], ['link'], ['clean']];

/**
 * Format a decimal-hours value as "Xh Ym".
 * @param {number} value - Value in decimal hours
 * @returns {string}
 */
function formatHoursMinutes(value) {
  const totalMinutes = Math.round(value * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${m}m`;
}

/**
 * Pure predicate: is at least one income-call flag enabled in option_field?
 * @param {object} optionField the session.option_field object (or {})
 * @returns {boolean}
 */
function hasIncomeCallEnabled(optionField) {
  if (!optionField?.income_calls) {
    return false;
  }
  return Object.values(optionField.income_calls).some(call => call === true);
}

/**
 * Pure: group added/removed relation records by underscored model name.
 * @param {Array} relations records with constructor.modelName and id
 * @returns {Array<{model: string, ids: Array}>}
 */
function groupRelationsByModel(relations) {
  const grouped = [];
  relations?.forEach(r => {
    const modelName = underscore(r.constructor?.modelName);
    const existing = grouped.find(g => g.model === modelName);
    if (existing) {
      existing.ids.push(r.id);
    } else {
      grouped.push({
        model: modelName,
        ids: [r.id]
      });
    }
  });
  return grouped;
}

/**
 * Pure: build the next `lastSavedRecord` structure after a save/destroy,
 * capping the per-model history at LAST_SAVED_RECORD_MAX entries.
 * Returns null when the record is not trackable (caller must not set()).
 * @param {object|null} lastSavedRecord current controller value
 * @param {object} record the saved record
 * @param {Array|null} result the saveAll result payload (id fallback)
 * @param {string} operation 'update' | 'destroy'
 * @param {number} [now] timestamp override (tests)
 * @returns {object|null}
 */
function appendLastSavedRecord(lastSavedRecord, record, result = null, operation = 'update', now = Date.now()) {
  const modelName = record?.constructor?.modelName;
  const unavailableForUpdate = operation !== 'destroy' && (record?.isDeleted || record?.isDeleting);
  if (!modelName || !record || unavailableForUpdate) {
    return null;
  }
  const base = lastSavedRecord || {};
  const operationData = base[operation] || {};
  const modelArray = operationData[modelName] || [];
  const id = record.id ? parseInt(record.id) : result?.[0]?.data?.id || null;
  if (!id) return null;
  const updatedData = {
    id,
    timestamp: now
  };
  return {
    ...base,
    [operation]: {
      ...operationData,
      [modelName]: [...modelArray, updatedData].slice(-LAST_SAVED_RECORD_MAX)
    }
  };
}

/**
 * Pure predicate: should the "new version available" notification be shown?
 * @param {string} newVersion
 * @param {string} currentVersion
 * @returns {boolean}
 */
function shouldShowVersionNotification(newVersion, currentVersion) {
  return newVersion && currentVersion && newVersion !== currentVersion;
}

/**
 * Pure predicate: should an already-booted tab ask /check again?
 * The boot check runs once, so a tab left open across a release kept the old
 * bundle (and its old URLs) until a manual reload. Re-checking is skipped when
 * the tab is hidden (no /check from background tabs), when the reload notice is
 * already up (no second notice), and when the last check is younger than minGapMs.
 * @param {{hidden: boolean, alreadyShown: boolean, lastCheckAt: number|null, now: number, minGapMs: number}} state
 * @returns {boolean}
 */
function shouldRecheckVersion({
  hidden,
  alreadyShown,
  lastCheckAt,
  now,
  minGapMs
}) {
  if (hidden || alreadyShown) return false;
  return lastCheckAt == null || now - lastCheckAt >= minGapMs;
}

/**
 * Pure predicate: should the SDI notification be shown to this user?
 * @param {object} me
 * @param {object} sdi
 * @returns {boolean}
 */
function shouldShowSdiNotification(me, sdi) {
  return me.organization_admin || me.role?.is_admin || sdi.contact_id === parseInt(me.id);
}

/**
 * Pure: the effective screensaver idle time in MINUTES for a stored
 * `screensaver.idle_time` value (number or numeric string from the input).
 * Missing, non-numeric, non-positive or out-of-range (> MAX_IDLE_MINUTES, see
 * there) values resolve to DEFAULT_IDLE_MINUTES.
 * @param {*} value the stored idle_time
 * @returns {number}
 */
function resolveIdleMinutes(value) {
  const minutes = parseFloat(value);
  if (!Number.isFinite(minutes) || minutes <= 0 || minutes > MAX_IDLE_MINUTES) {
    return DEFAULT_IDLE_MINUTES;
  }
  return minutes;
}

/**
 * Pure: compute the idle timeout (ms) from the session option_field.
 * @param {object} optionField the session.option_field object
 * @returns {number|null}
 */
function calculateIdleTimeout(optionField) {
  if (!optionField?.screensaver) {
    return DEFAULT_IDLE_TIMEOUT;
  }
  if (optionField.screensaver.disabled) {
    return null;
  }
  return resolveIdleMinutes(optionField.screensaver.idle_time) * 60000;
}

/**
 * Pure: build the LiveChat "fresh properties" object from the user.
 * @param {object} me the current user record
 * @param {object} params
 * @param {object} params.apiConfig the getConfig(this).APP object
 * @param {object} params.currentVersion the controller currentVersion object
 * @returns {object}
 */
function buildFreshProperties(me, {
  apiConfig,
  currentVersion
}) {
  const firstName = me.first_name || me.ragione_sociale;
  const organizationName = me.organization?.organization;
  return {
    id: me.id,
    firstName,
    lastName: me.last_name,
    email: me.mail,
    phone: me.mobile,
    ruolo: me.role?.role_name,
    'Organization id': `${me.organization?.organization}-${me.organization?.id}`,
    'Organization name': organizationName,
    'Organization admin': me.organization_admin,
    'Organization Trial Plan': me.organization?.subscription?.trial,
    'Organization Plan': me.organization?.subscription?.plan?.name,
    'Organization Product': apiConfig.isClinic ? 'Beebeedoc' : apiConfig.isSkiSchool ? 'Beebeeski' : 'Beebeeboard',
    'Organization Expire Plan': me.organization?.subscription?.expire_date ? moment(me.organization.subscription.expire_date).format('MM/DD/YYYY') : null,
    'Organization Bill Cycle': me.organization?.subscription?.month_billing_cycle ? 'Mensile' : 'Annuale',
    'Beebeeboard URI': `https://${organizationName}.beebeeboard.com`,
    Versione: `${currentVersion.version} (${currentVersion.commit})`
  };
}

export { DEFAULT_IDLE_MINUTES, DEFAULT_IDLE_TIMEOUT, LAST_SAVED_RECORD_MAX, MAX_IDLE_MINUTES, QUILL_TOOLBAR_CONFIG, ROUTE_MAPPING, appendLastSavedRecord, buildFreshProperties, calculateIdleTimeout, formatHoursMinutes, groupRelationsByModel, hasIncomeCallEnabled, resolveIdleMinutes, shouldRecheckVersion, shouldShowSdiNotification, shouldShowVersionNotification };
//# sourceMappingURL=application-helpers.js.map
